import {getHostInfo, actionTypes, allLogTypes as logTypes} from '@servicenow/ui-internal';
import log from './log';
import {POST_MESSAGE, MESSAGE_RECEIVED, SET_TARGET_WINDOW} from './constants';

const targetWindows = new WeakMap();
window.addEventListener('message', receiveMessage, false);

function getComponentIdToCallback(targetWindow, targetOrigin) {
	const targetOriginToComponentIdMap = targetWindows.get(targetWindow);
	if (!targetOriginToComponentIdMap) return;

	return {
		...targetOriginToComponentIdMap[targetOrigin],
		...targetOriginToComponentIdMap['*']
	};
}

function receiveMessage({data, origin, source}) {
	const componentIdToCallback = getComponentIdToCallback(source, origin);
	if (!componentIdToCallback) return;

	const callbacks = Object.values(componentIdToCallback);
	for (const callback of callbacks) callback(data);
}

function addPostMessageListener({componentId, targetWindow, targetOrigin}, callback) {
	let targetOriginToComponentIdMap = targetWindows.get(targetWindow);
	if (!targetOriginToComponentIdMap) {
		targetOriginToComponentIdMap = {};
		targetWindows.set(targetWindow, targetOriginToComponentIdMap);
	}

	let componentIdToCallback = targetOriginToComponentIdMap[targetOrigin];
	if (!componentIdToCallback) {
		componentIdToCallback = {};
		targetOriginToComponentIdMap[targetOrigin] = componentIdToCallback;
	}

	componentIdToCallback[componentId] = callback;
}

function removePostMessageListener({componentId, targetWindow, targetOrigin}) {
	const componentIdToCallback = getComponentIdToCallback(targetWindow, targetOrigin);

	if (componentIdToCallback && componentId in componentIdToCallback)
		delete componentIdToCallback[componentId];
}

export default function createPostMessageBehavior({
	postMessageAction = POST_MESSAGE,
	messageReceivedAction = MESSAGE_RECEIVED,
	setTargetWindowAction = SET_TARGET_WINDOW,
	targetOrigin
}) {
	if (!targetOrigin)
		log('targetOrigin must be set', {level: logTypes.ERROR, origin: 'createPostMessageBehavior'});

	return {
		name: 'postMessageBehavior',
		initialState: {
			targetWindow: null
		},
		actionHandlers: {
			[setTargetWindowAction]({updateState, action, properties, dispatch}) {
				const {targetWindow} = action.payload;
				const componentId = properties.nowId;

				updateState(
					{
						path: 'targetWindow',
						value: targetWindow,
						operation: 'set',
						shouldRender: false
					},
					{SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED: true}
				);

				if (targetWindow && componentId) {
					addPostMessageListener({componentId, targetWindow, targetOrigin}, data => {
						dispatch(messageReceivedAction, data);
					});
				} else {
					removePostMessageListener({componentId, targetWindow, targetOrigin});
				}
			},
			[postMessageAction]({state, action, host}) {
				const {targetWindow} = state;
				if (targetWindow) {
					targetWindow.postMessage(action.payload, targetOrigin);
				} else {
					const {tagName, componentId} = getHostInfo(host);
					log(
						`${postMessageAction} could not post message to targetWindow because targetWindow is not defined.`,
						{
							tagName,
							componentId,
							host,
							origin: `${postMessageAction}`
						}
					);
				}
			},
			[actionTypes.COMPONENT_DISCONNECTED]({properties, state: {targetWindow}}) {
				const componentId = properties.nowId;
				if (targetWindow && componentId)
					removePostMessageListener({componentId, targetWindow, targetOrigin});
			}
		}
	};
}
